import { useEffect, useRef, useState } from 'react';
import { BarChart, Bar, Rectangle, XAxis, YAxis, Tooltip, Legend, LabelList } from 'recharts';

import useTheme from '../../../../hooks/useTheme';
import useFormatting from '../../../../hooks/useFormatting';
import { chartFont, widgetSize } from '../../widgetTypography';

const BarChartWidget = ({ widgetData, chartData }) => {
    const { theme } = useTheme()
    const containerRef = useRef(null)
    const [dims, setDims] = useState({ width: 0, height: 0 })

    useEffect(() => {
        const observer = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect
            if (width > 0 && height > 0) {
                setDims({ width, height })
            }
        })
        if (containerRef.current) observer.observe(containerRef.current)
        return () => observer.disconnect()
    }, [])

    const primaryColor = theme.chart.fillColor
    const activeColor = theme.chart.activeFillColor
    const secondaryColor = theme.secondary.main
    const activeSecondaryColor = theme.secondary.active

    const fillColor = theme.secondary.main
    const activeFillColor = theme.secondary.active

    const { formatString } = useFormatting()
    const { bars = [] } = widgetData || {}
    const xAxisKey = widgetData?.xAxisKey || 'key'
    const labelFormat = widgetData?.labelFormat || 'percentage'
    // `orientation: 'horizontal'` lays the bars along the x axis, one per row,
    // with the names down the side — for a ranking whose names are too long to
    // sit under a column.
    const horizontal = widgetData?.orientation === 'horizontal'
    const barRadius = horizontal ? [0, 5, 5, 0] : [5, 5, 0, 0]
    // A name longer than the side allows is cut; the tooltip has it whole.
    const categoryWidth = widgetData?.categoryWidth || 140
    const maxNameLength = Math.floor(categoryWidth / 7)
    const formatName = (name = '') => name.length > maxNameLength ? `${name.slice(0, maxNameLength - 1)}…` : name

    const formatters = {
        amount: (value) => formatString(value, 'roundedAmount'),
        percentage: (value) => `${(value / 1000).toFixed(1)}%`,
    };

    const formatLabel = (value) => {
        const formatter = formatters[labelFormat] || ((v) => `${v}`);
        return formatter(value) === '0' ? '' : formatter(value);
    };

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
            {dims.width > 0 && dims.height > 0 && (
                <BarChart
                    width={dims.width}
                    height={dims.height}
                    data={chartData}
                    layout={horizontal ? 'vertical' : 'horizontal'}
                    margin={{
                        top: 15,
                        right: horizontal ? 60 : 10,
                        left: 10,
                        bottom: horizontal ? 10 : 35,
                    }}
                >
                    <XAxis dataKey={horizontal ? undefined : xAxisKey}
                        type={horizontal ? 'number' : 'category'}
                        hide={horizontal}
                        tick={{
                            fontSize: chartFont.tick,
                            fill: theme.font.color,
                            stroke: theme.font.color,
                            strokeWidth: 0
                        }}
                    />
                    <YAxis dataKey={horizontal ? xAxisKey : undefined}
                        type={horizontal ? 'category' : 'number'}
                        width={horizontal ? categoryWidth : 60}
                        interval={horizontal ? 0 : undefined}
                        tickFormatter={horizontal ? formatName : undefined}
                        tick={{
                            fontSize: chartFont.tick,
                            fill: theme.font.color,
                            stroke: theme.font.color,
                            strokeWidth: 0
                        }}
                    />
                    <Tooltip contentStyle={{ fontSize: widgetSize.label, backgroundColor: theme.card.selected, padding: '10px', borderRadius: '1rem' }} cursor={{ fill: theme.card.active, opacity: 0.4 }} />
                    {widgetData?.legend !== false && <Legend wrapperStyle={{ fontSize: widgetSize.label }} />}

                    {bars.map((bar, index) => {
                        const fill = bar.fill || primaryColor
                        const activeFill = bar.fillActive || activeColor
                        return <Bar name={bar.name} key={index} dataKey={bar.dataKey}
                            fill={fill === 'primary' ? primaryColor : fill === 'secondary' ? secondaryColor : fill === undefined ? fillColor : fill}
                            radius={barRadius}
                            activeBar={<Rectangle
                                fill={fill === 'primary' ? activeColor : fill === 'secondary' ? activeSecondaryColor : fill === undefined ? activeFillColor : activeFill}
                                radius={barRadius} />} >
                            <LabelList dataKey={bar.dataKey} position={horizontal ? 'right' : 'top'}
                                style={{ fontSize: chartFont.label }} formatter={formatLabel} />
                        </Bar>
                    })}
                </BarChart>
            )}
        </div>
    )
}

export default BarChartWidget
